import * as React from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  IconButton,
  LinearProgress,
  Box,
  Alert,
  Stack,
} from "@mui/material";
import NoDataIcon from "./NoDataIcon";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { ContentCopy, Download } from "@mui/icons-material";
import { useState } from "react";
import { ReactComponent as ArrowDownPurple } from "../../assets/arrow-down-purple.svg";
import { ReactComponent as ArrowUpPurpleDark } from "../../assets/arrow-up-purple-dark.svg";
import { ReactComponent as SortColumn } from "../../assets/sort-column.svg";
import CreatedChip from "../jobStates/CreatedChip";
import CompletedChip from "../jobStates/CompletedChip";
import ErrorChip from "../jobStates/ErrorChip";
import ProgressChip from "../jobStates/ProgressChip";
import UploadingChip from "../jobStates/UploadingChip";
import { Pagination } from "./PaginationData";
import CopyIcon from "../icons/CopyIcon";
import RightIcon from "../icons/RightIcon";
import ByteChip from "../icons/ByteChip";
import BracketIcon from "../icons/BracketIcon";
import DeleteBinIcon from "../icons/DeleteBinIcon";
import DuplicateIcon from "../icons/DuplicateIcon";
import NewIcon from "../icons/NewIcon";
import PlayIcon from "../icons/PlayIcon";
import CustomModal from "./modal/CustomModal";
import StopIcon from "../icons/StopIcon";
import StateChip from "../jobStates/StateChips";
import SquareIcon from "../icons/SquareIcon";
import SortDefaultIcon from "../icons/SortDefaultIcon";
import SortAscIcon from "../icons/SortAscIcon";
import SortDescIcon from "../icons/SortDescIcon";
import CorrectIcon from "../icons/CorrectIcon";
import LoadingIcon from "../icons/LoadingIcon";
import FailedIcon from "../icons/FailedIcon";

export interface DisplayDataTableProps {
  data: any[];
  columnsNames: string[];
  columnsOrder: string[];
  columnsSortable?: string[];
  iconNoData?: any;
  messageNoData?: string;
  displayTableIfNoData?: boolean;
  pagination?: Pagination;
  tableCellNoBorder?: boolean;
  transactionColumns?: string[];
  abortJob?: any;
  copyJob?: any;
  deleteJob?: any;
  downloadResults?: any;
  canAbort?: any;
  canEdit?: any;
  isComplete?: any;
  canDelete?: any;
  hasJobActions?: boolean;
  columnsJobState?: string[];
  onColumnClick?: any;
  columnsProgress?: string[];
  currentPageSize: number;
  columnsTemplate?: string[];
  columnsCopy?: string[];
  columnsCost?: string[];
  ExpandComponent?: any;
  getJobId?: (id: string, info: string) => void;
  isLoading?: any;
  jobDetailData?: any;
  resultData?: any;
  viewObject?: any;
  tableInfo?: string;
  columnsAction?: string[];
  runJob?: any;
}

function isValidHttpUrl(string: string | URL) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

const DisplayDataTable = ({ ...props }: DisplayDataTableProps) => {
  const {
    data,
    currentPageSize,
    columnsNames,
    columnsOrder,
    iconNoData,
    messageNoData,
    displayTableIfNoData,
    tableCellNoBorder,
    transactionColumns,
    onColumnClick,
    columnsProgress,
    pagination,
    isLoading,
  } = props;
  const {
    abortJob,
    copyJob,
    canAbort,
    canEdit,
    isComplete,
    downloadResults,
    canDelete,
    deleteJob,
    hasJobActions,
    columnsJobState,
    columnsSortable,
    columnsTemplate,
    columnsCopy,
    columnsCost,
    ExpandComponent,
    getJobId,
    jobDetailData,
    viewObject,
    tableInfo,
    columnsAction,
    runJob,
  } = props;

  const [expandedRow, setExpandedRow] = useState("");

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const statePill = (state: string) => {
    switch (state) {
      case "NEW":
        return <CreatedChip></CreatedChip>;
      case "COMPLETE":
        return (
          <StateChip
            icon={<CorrectIcon color="success" size="xs" />}
            label="Complete"
            textColor="#067701"
          />
        );
      case "FAILED":
        return (
          <StateChip
            icon={<FailedIcon size="xs" color="error" />}
            label="Failed"
            textColor="#B00000"
          />
        );
      case "UPLOADING":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="secondary"
            label="Uploading"
            textColor="#3638C4"
          />
        );
      case "STARTED":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="secondary"
            label="Started"
            textColor="#3638C4"
          />
        );
      case "PARTITIONING":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="primary"
            label="Partitioning"
            textColor="#B027CE"
          />
        );
      case "PARTITIONED":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="primary"
            label="Partitioned"
            textColor="#B027CE"
          />
        );
      case "TASKS_COMPLETED":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="info"
            label="Tasks Completed"
            textColor="#FECB00"
          />
        );
      case "MERGING":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="warning"
            label="Merging"
            textColor="#D97D62"
          />
        );
      case "MERGED":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="warning"
            label="Merged"
            textColor="#D97D62"
          />
        );
      case "DOWNLOADING":
        return (
          <StateChip
            icon={<LoadingIcon size="xs" color="secondary" />}
            color="info"
            label="Downloading"
            textColor="#FECB00"
          />
        );
      default:
        return <CreatedChip></CreatedChip>;
    }
  };

  const progressBar = (progress: number) => {
    return (
      <Box sx={{ width: "200px" }}>
        <LinearProgress
          variant="determinate"
          color={"primary"}
          value={progress}
        />
      </Box>
    );
  };
  const clampText = (text: string) => {
    return (
      <Box
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 1,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };
  const handleCopy = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedId(value);
        setTimeout(() => {
          setCopiedId(null);
        }, 1500);
      })
      .catch((error) => {
        console.error("Failed to copy text:", error);
      });
  };
  const copyIdBox = (value: string) => {
    return (
      <Box
        sx={{
          border: "1px solid #A3A3A3",
          display: "flex",
          justifyContent: copiedId === value ? "space-between" : "normal",
          alignItems: "center",
          columnGap: "4px",
          padding: "3px 10px 3px 10px",
          borderRadius: "16px",
          width: "fit-content",
        }}
      >
        {copiedId === value ? (
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
            }}
          >
            Copied!
          </Typography>
        ) : (
          <input
            type="text"
            id="copyText"
            value={value}
            readOnly
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100px",
              userSelect: "none",
              pointerEvents: "none",
              fontFamily: "Gilroy",
              fontWeight: 400,
              fontSize: "10px",
              lineHeight: "16px",
              fontStyle: "italic",
            }}
          />
        )}
        {copiedId === value ? (
          <RightIcon color="textPrimary" size="xs" />
        ) : (
          <IconButton sx={{ p: 0 }} onClick={() => handleCopy(value)}>
            <CopyIcon
              color="textPrimary"
              size="xxs"
              style={{
                cursor: "pointer",
              }}
            />
          </IconButton>
        )}
      </Box>
    );
  };
  const byteChipWithText = (text: string) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "1px",
        }}
      >
        <ByteChip size="xs" />
        <Typography>{text}</Typography>
      </Box>
    );
  };

  const icons = [
    <SortDefaultIcon size="xxs" />,
    <SortDescIcon size="xxs" />,
    <SortAscIcon size="xxs" />,
  ];

  // Track sort icon state per column
  const [sortStates, setSortStates] = useState<Record<string, number>>({});
  const [columnName, setColumnName] = useState<string>("");
  const handleColumnClick = (colName: string) => {
    setSortStates((prev) => {
      const current = prev[colName] ?? 0;
      const next = (current + 1) % icons.length;
      return { ...prev, [colName]: next };
    });
    setColumnName(colName);

    // if you want to pass this on
  };
  React.useEffect(() => {
    if (Object.keys(sortStates).length === 0 || !columnName) return;
    onColumnClick(columnName, sortStates);
  }, [sortStates, columnName]);
  const headerTable = (
    <TableHead>
      <TableRow>
        {columnsOrder && columnsOrder.length > 0 ? (
          columnsOrder.map((colName: any, index: number) => (
            <TableCell
              key={colName}
              style={{
                width:
                  colName === "expandIcon" || colName === "jobState"
                    ? "10px"
                    : "150px",
              }}
              // className={
              //   index === 0
              //     ? "table-header-left-cell"
              //     : index === columnsNames.length - 1
              //     ? "table-header-right-cell"
              //     : ""
              // }
            >
              {columnsSortable && columnsSortable.indexOf(colName) >= 0 ? (
                <div
                  className={"container-horizontal content-space-between"}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleColumnClick(colName)}
                >
                  <div>
                    <Typography
                      variant={"label2"}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {columnsNames[index]}
                    </Typography>
                  </div>
                  <div
                    style={{ marginLeft: "5px" }}
                    className={"container-vertical content-center"}
                  >
                    {icons[sortStates[colName] ?? 0]}
                  </div>
                </div>
              ) : (
                <Typography variant="label2">{columnsNames[index]}</Typography>
              )}
            </TableCell>
          ))
        ) : (
          <TableCell>Missing column names</TableCell>
        )}
      </TableRow>
    </TableHead>
  );

  const filteredData = pagination
    ? data?.filter(
        (_, i) =>
          i >= pagination.offset && i < pagination.offset + currentPageSize
      )
    : data;
  const handleExpandRow = (baseKey: string) => {
    const isExpanding = expandedRow !== baseKey; // Check if it's expanding

    setExpandedRow(isExpanding ? baseKey : "");

    if (isExpanding && getJobId && tableInfo) {
      getJobId(baseKey, tableInfo);
    }
  };
  const actionBox = (state: string, id: string) => {
    if (state === "JOB_STATE_NEW") {
      return (
        <IconButton onClick={() => runJob(id)}>
          <PlayIcon size="xs" color="primary" />
        </IconButton>
      );
    } else {
      return (
        <IconButton onClick={() => abortJob(id)}>
          <SquareIcon size="xs" color="error" />
        </IconButton>
      );
    }
  };
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const handleOpenDeleteModal = (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };
  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteId(undefined);
  };
  const handleDelete = () => {
    if (deleteId) {
      deleteJob(deleteId);
      setDeleteId(undefined);
      setOpenDeleteModal(false);
    }
  };

  return (
    <Box className={"container-vertical content-center-large"}>
      <CustomModal
        defaultLogoImage={false}
        handleClose={closeDeleteModal}
        openModal={openDeleteModal}
      >
        <Stack
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <StopIcon size="large" color="info" />
          <Typography variant="h5" sx={{ color: "#000271", fontSize: "18px" }}>
            Are you sure you want to delete this job?
          </Typography>
          <Typography
            sx={{
              width: "80%",
              textAlign: "center",
            }}
          >
            If you delete this job record (ID:
            {deleteId}), it cannot be recovered.
          </Typography>
          <Stack direction={"row"} columnGap={4}>
            <Button
              type="button"
              sx={{
                mt: 2,
              }}
              variant="outlined"
              onClick={closeDeleteModal}
            >
              Cancel
            </Button>
            <Button
              type="button"
              sx={{
                mt: 2,
              }}
              variant="contained"
              onClick={handleDelete}
              color="error"
            >
              Delete Job
            </Button>
          </Stack>
        </Stack>
      </CustomModal>
      {data && data.length > 0 ? (
        <>
          <Table className={"table-large"}>
            {headerTable}

            <TableBody>
              {filteredData.map((row: any) => {
                const baseKey =
                  row.id || Object.keys(row).join("-") + data.indexOf(row);

                return (
                  <React.Fragment key={baseKey}>
                    <TableRow>
                      {columnsOrder.map((k: any) =>
                        Object.keys(row).indexOf(k) >= 0 ? (
                          <TableCell
                            style={
                              tableCellNoBorder || expandedRow == baseKey
                                ? {
                                    borderBottom: "none",
                                    padding: "1em",
                                  }
                                : {
                                    padding: "1em",
                                  }
                            }
                            key={baseKey + k}
                            align="left"
                          >
                            {isValidHttpUrl(row[k]) ? (
                              <a href={row[k]}>
                                {row[k].length >= 100
                                  ? row[k].substring(0, 100) + "..."
                                  : row[k]}
                              </a>
                            ) : columnsProgress &&
                              columnsProgress.indexOf(k) >= 0 ? (
                              progressBar(row[k])
                            ) : columnsJobState &&
                              columnsJobState.indexOf(k) >= 0 ? (
                              statePill(row[k])
                            ) : columnsTemplate &&
                              columnsTemplate.indexOf(k) >= 0 ? (
                              clampText(row[k])
                            ) : columnsCopy && columnsCopy.indexOf(k) >= 0 ? (
                              copyIdBox(row[k])
                            ) : columnsCost && columnsCost.indexOf(k) >= 0 ? (
                              byteChipWithText(row[k])
                            ) : columnsAction &&
                              columnsAction.indexOf(k) >= 0 ? (
                              actionBox(row[k], row.id)
                            ) : (
                              <Typography
                                color={
                                  transactionColumns &&
                                  transactionColumns.indexOf(k) >= 0
                                    ? row[k] < 0
                                      ? "error.main"
                                      : "success.main"
                                    : ""
                                }
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "14px",
                                }}
                              >
                                {row[k]}
                              </Typography>
                            )}
                          </TableCell>
                        ) : k == "expandIcon" ? (
                          <TableCell
                            style={
                              tableCellNoBorder || expandedRow == baseKey
                                ? {
                                    borderBottom: "none",
                                    padding: "1em",
                                  }
                                : {
                                    padding: "1em",
                                  }
                            }
                            key={baseKey + "expandIcon"}
                          >
                            <IconButton
                              onClick={() => handleExpandRow(baseKey)}
                            >
                              {expandedRow == baseKey ? (
                                <ArrowUpPurpleDark></ArrowUpPurpleDark>
                              ) : (
                                <ArrowDownPurple></ArrowDownPurple>
                              )}
                            </IconButton>
                          </TableCell>
                        ) : (
                          <></>
                        )
                      )}
                    </TableRow>
                    {expandedRow == baseKey && hasJobActions == true && (
                      <>
                        <TableRow>
                          {/* fill all the cells to make a row and then the last cell put the icons*/}
                          {
                            <TableCell
                              align={"left"}
                              colSpan={columnsOrder.length}
                              sx={{
                                border: "none",

                                px: 6,
                                py: -20,
                              }}
                            >
                              {ExpandComponent && ExpandComponent}
                            </TableCell>
                          }
                        </TableRow>
                        <TableRow>
                          {
                            <TableCell
                              align={"right"}
                              colSpan={columnsOrder.length}
                            >
                              <Stack
                                direction={"row"}
                                justifyContent={"flex-end"}
                                gap={2}
                                mb={3}
                              >
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    columnGap: 1,
                                  }}
                                  onClick={() => viewObject(jobDetailData)}
                                >
                                  <BracketIcon size="xs" color="primary" />
                                  <Typography color="primary">
                                    View Job Object
                                  </Typography>
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    columnGap: 1,
                                  }}
                                  onClick={() => copyJob(row.id)}
                                >
                                  <DuplicateIcon size="xs" color="primary" />
                                  <Typography color="primary">
                                    Duplicate
                                  </Typography>
                                </IconButton>
                                {(isComplete && isComplete(row.state)) ||
                                (canEdit && canEdit(row.state)) ? (
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      columnGap: 1,
                                    }}
                                    onClick={() =>
                                      handleOpenDeleteModal(row.id)
                                    }
                                  >
                                    <DeleteBinIcon size="xs" color="error" />
                                    <Typography color="error">
                                      Delete
                                    </Typography>
                                  </IconButton>
                                ) : null}

                                {canAbort && canAbort(row.state) ? (
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      columnGap: 1,
                                    }}
                                    onClick={() => abortJob(row.id)}
                                  >
                                    <SquareIcon size="xs" color="error" />
                                    <Typography mt={"3px"} color="#B00000">
                                      Abort
                                    </Typography>
                                  </IconButton>
                                ) : null}
                                {canEdit && canEdit(row.state) ? (
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      columnGap: 1,
                                    }}
                                    onClick={() => runJob(row.id)}
                                  >
                                    <PlayIcon size="xs" color="success" />
                                    <Typography mt={"3px"} color="#059B00">
                                      Start
                                    </Typography>
                                  </IconButton>
                                ) : null}
                                {/* {isComplete && isComplete(row.state) ? (
                                  <IconButton
                                    sx={{
                                      mx: 2,
                                    }}
                                    size="small"
                                    color="primary"
                                    onClick={() => downloadResults(row.id)}
                                  >
                                    <Download fontSize="inherit" />
                                  </IconButton>
                                ) : null} */}
                              </Stack>
                            </TableCell>
                          }
                        </TableRow>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </>
      ) : (
        <div className={"container-vertical content-center-large"}>
          {displayTableIfNoData && (
            <Table className={"table-large"}>{headerTable}</Table>
          )}

          {iconNoData && (
            <NoDataIcon icon={iconNoData} message={messageNoData}></NoDataIcon>
          )}
        </div>
      )}
    </Box>
  );
};

export default DisplayDataTable;
